import { useMemo, useState } from 'react';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UpcomingRoundedIcon from '@mui/icons-material/UpcomingRounded';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { useMerchantPortalSettingsSectionVisibility } from '@/shared/navigation/merchantPortalNavVisibility';
import { AccessRestricted, PageContainer } from '@/shared/ui';

import { SETTINGS_GROUPS, settingsNavForGroup } from '../settingsNav';
import type { SettingsNavItem } from '../settingsNav';

/**
 * Store-settings overview: a permission-aware, **grouped** and searchable index
 * of every settings domain. Domains are organised into meaningful categories
 * (Store, Selling, Fulfillment, …) so the page is scannable instead of a flat
 * wall of tiles. Domains the employee cannot view are omitted entirely; only
 * preview domains (no backend yet, so nobody holds their code) render as
 * coming-soon teasers.
 */
export function SettingsIndexPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = usePermissions();
  const sectionVisibility = useMerchantPortalSettingsSectionVisibility();
  const [filter, setFilter] = useState('');
  const query = filter.trim().toLowerCase();

  const groups = useMemo(
    () =>
      SETTINGS_GROUPS.map((group) => ({
        group,
        items: settingsNavForGroup(group).filter(
          (nav) =>
            !nav.hiddenFromUi &&
            // Domains the employee cannot view are omitted, not dimmed. A locked row advertises a
            // capability and then refuses it — for someone whose whole job is the warehouse, that
            // turned Settings into thirty greyed-out rows with one live entry buried among them.
            // Previews stay: they have no backend at all, so nobody holds their code, and hiding
            // "not viewable" would delete the coming-soon teasers from everyone.
            (hasPermission(nav.viewPermission) || nav.preview) &&
            (!query ||
              t(nav.label).toLowerCase().includes(query) ||
              t(nav.description).toLowerCase().includes(query)),
        ),
      }))
        .filter((entry) => (entry.group.featureKey ? sectionVisibility[entry.group.featureKey] : true))
        .filter((entry) => entry.items.length > 0),
    [hasPermission, query, sectionVisibility, t],
  );

  return (
    <PageContainer
      title={t('settingsIndex.title')}
      subtitle={t('settingsIndex.subtitle')}
    >
      <TextField
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        placeholder={t('settingsIndex.searchPlaceholder')}
        size="small"
        aria-label={t('settingsIndex.searchAriaLabel')}
        sx={{ mb: 4, maxWidth: { sm: 460 } }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      {groups.length === 0 && query ? (
        <Typography color="text.secondary" sx={{ py: 4 }}>
          {t('settingsIndex.noMatches', { filter })}
        </Typography>
      ) : groups.length === 0 ? (
        // Not "no settings match """ — nothing was searched for. Now that unviewable domains
        // are omitted rather than dimmed, an employee whose only domains sit in a staged-off group
        // reaches a genuinely empty hub, and a search-result message would read as a bug.
        <AccessRestricted />
      ) : (
        <Stack spacing={4}>
          {groups.map(({ group, items }) => (
            <Box key={group.key} component="section">
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  aria-hidden
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    flexShrink: 0,
                    color: '#fff',
                    background: (t) =>
                      `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`,
                  }}
                >
                  {group.icon}
                </Box>
                <Box>
                  <Typography variant="h6">{t(group.label)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(group.description)}
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    xl: 'repeat(3, 1fr)',
                  },
                }}
              >
                {items.map((nav) => (
                  <SettingsDomainCard
                    key={nav.key}
                    nav={nav}
                    canView={hasPermission(nav.viewPermission)}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </PageContainer>
  );
}

interface SettingsDomainCardProps {
  nav: SettingsNavItem;
  canView: boolean;
}

function SettingsDomainCard({ nav, canView }: SettingsDomainCardProps) {
  const { t } = useTranslation('settings');
  const comingSoon = !canView && Boolean(nav.preview);
  const content = (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ p: 2.5, height: '100%' }}>
      <Box
        aria-hidden
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 44,
          height: 44,
          borderRadius: 2.5,
          flexShrink: 0,
          color: canView ? '#fff' : 'text.disabled',
          background: (t) =>
            canView
              ? `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`
              : t.palette.action.hover,
        }}
      >
        {nav.icon}
      </Box>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight={700}>
            {t(nav.label)}
          </Typography>
          {comingSoon ? (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              icon={<UpcomingRoundedIcon sx={{ fontSize: 14 }} />}
              label={t('settingsIndex.comingSoonChip')}
            />
          ) : !canView ? (
            <Chip
              size="small"
              variant="outlined"
              icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
              label={t('settingsIndex.noAccessChip')}
            />
          ) : nav.preview ? (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              icon={<UpcomingRoundedIcon sx={{ fontSize: 14 }} />}
              label={t('settingsIndex.previewChip')}
            />
          ) : null}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t(nav.description)}
        </Typography>
      </Box>
      {canView || comingSoon ? (
        <ChevronRightRoundedIcon sx={{ color: 'text.disabled', alignSelf: 'center' }} />
      ) : null}
    </Stack>
  );

  return (
    <Card sx={{ opacity: canView ? 1 : 0.7 }}>
      {canView ? (
        <CardActionArea component={RouterLink} to={nav.path} sx={{ height: '100%', alignItems: 'stretch' }}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
}
